import { getVersion } from './util';
import { ERROR_CODE_PREVIOUS_CALL_IN_PROGRESS } from './emailTypeService';
import { DIALOG_CLOSED_MESSAGE, DialogSignInOptions, isDialogPromptIgnoredError } from './fallback/dialogSignIn';

/**
 * Diagnostics for the add-in startup sequence.
 *
 * Startup runs MSAL token acquisition -> item.saveAsync -> POST /api/link.
 * Every one of those steps used to collapse into the same
 * "Client could not get token" message, and most of them threw away the
 * underlying error, so a support screenshot could not tell them apart.
 *
 * Each failure now carries a stable code, the phase it happened in, and a
 * serialized detail block. Nothing here ever reads message content; the
 * mailbox addresses it does collect are there to answer the one question a
 * permission failure always raises, which mailbox refused.
 */

// Stable, user-quotable codes. Never reuse or renumber one: support tickets
// and log queries refer to them.
export const StartupErrorCode = {
    MSAL_INIT: 'MM-01',       // MSAL could not be created, or onSignIn threw unexpectedly
    INTERACTIVE: 'MM-02',     // acquireTokenPopup failed for a non-popup reason (consent, conditional access)
    DIALOG_FALLBACK: 'MM-03', // popup problem, and the Office Dialog API fallback also failed
    NO_TOKEN: 'MM-04',        // silent, popup and dialog all finished without a token
    SAVE_ITEM: 'MM-05',       // Office.context.mailbox.item.saveAsync reported a failure
    AFTER_SAVE: 'MM-06',      // saveAsync succeeded but reading the item id / starting the link threw
    LINK_REQUEST: 'MM-07',    // POST /api/link never returned a result (network, 5xx, CORS)
    LINK_REJECTED: 'MM-08',   // the server returned a failure result
    REAUTH_FAILED: 'MM-09',   // the server rejected the identity token twice
    AUTH_LOOP: 'MM-10',       // the client loop breaker tripped
};

// Strings are capped individually so one huge stack or HTML error page cannot
// crowd out the rest of the report
export const MAX_VALUE_LENGTH = 1000;
// Whole-report cap, matched by the server side of /api/log/clienterror
export const MAX_DIAGNOSTICS_LENGTH = 8000;

export interface StartupDiagnostics {
    code: string;
    phase: string;
    version: string;
    at: string;
    host: { [key: string]: any };
    detail?: any;
}

export const truncate = (value: string, maxLength: number): string =>
    typeof value === 'string' && value.length > maxLength
        ? value.substr(0, maxLength) + '...[truncated]'
        : value;

export const safeStringify = (value: any, indent?: number): string => {
    if (value === undefined) {
        return '(undefined)';
    }
    const seen = new WeakSet();
    try {
        const json = JSON.stringify(value, (key, inner) => {
            if (typeof inner === 'object' && inner !== null) {
                if (seen.has(inner)) {
                    return '[circular]';
                }
                seen.add(inner);
            }
            if (typeof inner === 'string') {
                return truncate(inner, MAX_VALUE_LENGTH);
            }
            if (typeof inner === 'function') {
                return '[function]';
            }
            return inner;
        }, indent);
        return json === undefined ? String(value) : json;
    } catch (ex) {
        return '[unserializable: ' + (ex && ex.message) + ']';
    }
};

// Error and its subclasses (MSAL BrowserAuthError, axios AxiosError,
// OfficeAsyncError) keep these on the prototype, so a plain
// JSON.stringify(ex) returns "{}" and the real cause is lost.
const ERROR_FIELDS = [
    'name', 'message', 'stack', 'code', 'errorCode', 'errorMessage', 'subError', 'correlationId',
];

// Axios attaches large objects here; the useful parts are lifted out by hand
const SKIP_OWN_FIELDS = ['response', 'config', 'request'];

export const serializeError = (ex: any): any => {
    if (ex === null || ex === undefined) {
        return { message: '(no error object)' };
    }
    if (typeof ex !== 'object') {
        return { message: String(ex) };
    }

    const out: { [key: string]: any } = {};

    for (const field of ERROR_FIELDS) {
        try {
            const value = ex[field];
            if (value !== undefined && value !== null && value !== '') {
                out[field] = value;
            }
        } catch (readEx) { /* property getters are allowed to throw */ }
    }

    try {
        if (ex.response) {
            out.httpStatus = ex.response.status;
            out.httpBody = ex.response.data;
        }
        if (ex.config && ex.config.url) {
            out.requestUrl = ex.config.url;
        }
    } catch (readEx) { /* not an axios error */ }

    // Anything else the object carries itself
    try {
        for (const key of Object.keys(ex)) {
            if (out[key] === undefined && SKIP_OWN_FIELDS.indexOf(key) < 0) {
                out[key] = ex[key];
            }
        }
    } catch (readEx) { /* exotic object */ }

    if (Object.keys(out).length === 0) {
        out.raw = safeStringify(ex);
    }
    return out;
};

export interface DialogFallbackAttempt {
    popupError: any;
    dialogError: any;
    dialogOptions: DialogSignInOptions;
    staleTokenPaths: string[];
    tokenSource: string | null;
    forceTokenRefresh: boolean;
    forceInteractive: boolean;
}

/**
 * The MM-03 detail block. The two errors alone could not tell a blocked popup
 * from a popup that worked and handed back an expired token (popupError is
 * then "(no error object)"), nor whether the user closed a sign-in window that
 * was asking for their password. Both had to be reconstructed by hand from a
 * support ticket.
 *
 * dialogPromptIgnored separates the case where the window never opened at all
 * because the user dismissed the host's own Allow/Ignore prompt.
 */
export const describeDialogFallbackFailure = (attempt: DialogFallbackAttempt): any => {
    const staleTokenPaths = attempt.staleTokenPaths || [];
    const popupOutcome = attempt.popupError != null
        ? 'failed'
        : staleTokenPaths.indexOf('popup') >= 0 ? 'expired-token' : 'no-token';
    const options = attempt.dialogOptions || {};

    return {
        popupError: serializeError(attempt.popupError),
        popupOutcome,
        dialogError: serializeError(attempt.dialogError),
        dialogClosedByUser: attempt.dialogError === DIALOG_CLOSED_MESSAGE,
        dialogPromptIgnored: isDialogPromptIgnoredError(attempt.dialogError),
        dialogForceFresh: !!options.forceFresh,
        dialogSelectAccount: !!options.selectAccount,
        staleTokenPaths,
        tokenSource: attempt.tokenSource,
        forceTokenRefresh: attempt.forceTokenRefresh,
        forceInteractive: attempt.forceInteractive,
    };
};

/**
 * True when the host failed the call only because another Office.js call on
 * the item was still running. This is the one condition worth retrying;
 * every other host error is a real failure.
 */
export const isTransientOfficeError = (result: any): boolean =>
    !!(result && result.error && result.error.code === ERROR_CODE_PREVIOUS_CALL_IN_PROGRESS);

/**
 * The host refused the call because the user may not write where the item
 * lives: a shared mailbox held without Full Access, a delegate with
 * folder-level rights only, or a draft under a sensitivity label that forbids
 * saving. Repeating the call always gets the same answer, so this is worth
 * separating from the other MM-05 causes (offline, mailbox full, item closed),
 * which a retry can fix.
 */
export const ERROR_CODE_INSUFFICIENT_ITEM_PERMISSIONS = 9038;

export const isInsufficientItemPermissionsError = (result: any): boolean =>
    !!(result && result.error && result.error.code === ERROR_CODE_INSUFFICIENT_ITEM_PERMISSIONS);

/**
 * What a permission refusal never says: which mailbox refused.
 *
 * The host error carries no mailbox at all, so a report could not tell a user
 * writing in their own mailbox from one writing in their manager's, and every
 * such ticket cost several rounds of questions to establish it. These two
 * host calls answer it directly.
 *
 * Both are best effort. The user is already looking at a failure, so each
 * call is capped, and anything that fails, is unsupported on the host or
 * never calls back leaves its field out instead of holding the report back.
 */
export interface MailboxOwnership {
    /** The mailbox Office bound the add-in to. */
    userEmail?: string;
    /** The address the draft is being written from. */
    fromEmail?: string;
    /** Owner of the shared mailbox or delegated folder the draft lives in. */
    ownerEmail?: string;
    /** Rights the host reports on that shared mailbox, as its bit field. */
    delegatePermissions?: number;
    /** True when the host says the draft is in a shared or delegated folder. */
    sharedMailbox?: boolean;
    /** True when the draft does not belong to the mailbox the add-in runs in. */
    mailboxMismatch?: boolean;
    fromError?: string;
    /** Why the host would not describe the folder. Its usual answer, on an
     * item that is not shared at all, is itself a useful negative. */
    sharedPropertiesError?: string;
}

// Two host calls on a failed startup, one after the other. Long enough for a
// host that is merely busy, short enough that a host which has stopped
// answering does not add noticeably to a failure the user is already reading.
export const MAILBOX_PROBE_TIMEOUT_MS = 2000;

const settleOnce = <T>(resolve: (value: T) => void): ((value: T) => void) => {
    let settled = false;
    return (value: T) => {
        if (settled) {
            return;
        }
        settled = true;
        resolve(value);
    };
};

type OwnershipErrorField = 'fromError' | 'sharedPropertiesError';

/**
 * Runs one Office.js probe, capped and unable to throw or reject. A host that
 * never calls back, or throws on the way in, reports itself in the probe's
 * own error field.
 */
const probe = (
    run: (done: (fragment: MailboxOwnership) => void) => void,
    errorField: OwnershipErrorField,
    label: string,
    timeoutMs: number): Promise<MailboxOwnership> =>
    new Promise<MailboxOwnership>((resolve) => {
        const done = settleOnce(resolve);
        const fail = (message: string) => done({ [errorField]: message } as MailboxOwnership);
        const handle = setTimeout(() => fail(`(${label} did not answer within ${timeoutMs}ms)`), timeoutMs);
        const finish = (fragment: MailboxOwnership) => {
            clearTimeout(handle);
            done(fragment || {});
        };
        try {
            run(finish);
        } catch (ex) {
            clearTimeout(handle);
            fail((ex && ex.message) || String(ex));
        }
    });

/**
 * Office.js reports status as the string "succeeded" at runtime, while the
 * type definitions declare AsyncResultStatus a numeric enum. Going through
 * the host's own constant keeps this right either way, and the literal is
 * only there for a host that has not published the enum yet.
 */
const isSucceeded = (result: any): boolean => {
    if (!result) {
        return false;
    }
    try {
        if (Office && Office.AsyncResultStatus) {
            return result.status === Office.AsyncResultStatus.Succeeded;
        }
    } catch (ex) { /* no Office object graph */ }
    return result.status === 'succeeded';
};

const officeErrorText = (result: any): string => {
    const error = result && result.error;
    if (!error) {
        return '(host reported a failure without an error object)';
    }
    return `${error.code != null ? error.code + ': ' : ''}${error.message || error.name || 'unknown'}`;
};

/** item.from, the address on the draft. Compose messages only (Mailbox 1.7). */
const probeFromAddress = (item: any, timeoutMs: number): Promise<MailboxOwnership> => {
    if (!item || !item.from || typeof item.from.getAsync !== 'function') {
        return Promise.resolve({ fromError: '(from is not available on this item)' });
    }
    return probe((done) => {
        item.from.getAsync((result: any) => {
            if (isSucceeded(result) && result.value && result.value.emailAddress) {
                done({ fromEmail: result.value.emailAddress });
            } else {
                done({ fromError: officeErrorText(result) });
            }
        });
    }, 'fromError', 'from', timeoutMs);
};

/**
 * item.getSharedPropertiesAsync, which names the owner of a shared mailbox or
 * delegated folder (Mailbox 1.8). On an item that is not shared the host
 * answers with an error, and that answer is worth keeping: it rules the whole
 * shared-mailbox explanation out.
 */
const probeSharedProperties = (item: any, timeoutMs: number): Promise<MailboxOwnership> => {
    let supported = false;
    try {
        supported = Office.context.requirements.isSetSupported('Mailbox', '1.8');
    } catch (ex) { /* older host, or no Office at all */ }

    if (!supported || !item || typeof item.getSharedPropertiesAsync !== 'function') {
        return Promise.resolve({ sharedPropertiesError: '(shared properties are not supported by this host)' });
    }

    return probe((done) => {
        item.getSharedPropertiesAsync((result: any) => {
            if (isSucceeded(result) && result.value) {
                const fragment: MailboxOwnership = { sharedMailbox: true };
                if (result.value.owner) {
                    fragment.ownerEmail = result.value.owner;
                }
                if (result.value.delegatePermissions != null) {
                    fragment.delegatePermissions = result.value.delegatePermissions;
                }
                done(fragment);
            } else {
                // The host's way of saying the item is not in a shared folder
                done({ sharedMailbox: false, sharedPropertiesError: officeErrorText(result) });
            }
        });
    }, 'sharedPropertiesError', 'shared properties', timeoutMs);
};

const sameAddress = (a?: string, b?: string): boolean =>
    !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * The mailbox the draft belongs to. The shared owner wins over the From
 * address: a delegate writing on someone's behalf has their own address on
 * the draft, and it is the owning mailbox that refused the save.
 */
export const writingMailbox = (ownership: MailboxOwnership): string =>
    (ownership && (ownership.ownerEmail || ownership.fromEmail)) || null;

/**
 * Asks the host which mailbox the draft belongs to. Never rejects: a probe
 * that cannot answer returns the reason in place of the value, and a report
 * that says the host refused to say is still better than one that never
 * asked.
 *
 * The calls run one after the other on purpose. Two Office.js calls in flight
 * on the same item is exactly the condition that produces 5100, and losing
 * the answer to a self-inflicted race would defeat the point.
 */
export const describeMailboxOwnership = async (
    timeoutMs: number = MAILBOX_PROBE_TIMEOUT_MS): Promise<MailboxOwnership> => {
    let item: any = null;
    const ownership: MailboxOwnership = {};

    try {
        item = Office.context.mailbox.item;
    } catch (ex) { /* no item, or no Office object graph */ }

    try {
        const userEmail = Office.context.mailbox.userProfile.emailAddress;
        if (userEmail) {
            ownership.userEmail = userEmail;
        }
    } catch (ex) { /* the profile is not always readable at startup */ }

    let from: MailboxOwnership = {};
    let shared: MailboxOwnership = {};
    try {
        from = await probeFromAddress(item, timeoutMs);
        shared = await probeSharedProperties(item, timeoutMs);
    } catch (ex) {
        // Reaching the probes at all can throw on an exotic host; whatever
        // was gathered before that is still worth reporting
    }

    const merged: MailboxOwnership = { ...ownership, ...from, ...shared };

    const writing = writingMailbox(merged);
    if (writing && merged.userEmail) {
        merged.mailboxMismatch = !sameAddress(writing, merged.userEmail);
    }

    return merged;
};

/**
 * One sentence naming the mailbox that refused, for the error the user reads.
 * Empty when the probes learned nothing, so the caller falls back to listing
 * the possible causes rather than stating a guess as fact.
 */
export const mailboxOwnershipHint = (ownership: MailboxOwnership): string => {
    if (!ownership) {
        return '';
    }

    const signedInAs = ownership.userEmail
        ? `, while MailMerge365 is running in ${ownership.userEmail}`
        : '';

    if (ownership.sharedMailbox && ownership.ownerEmail) {
        return `Outlook says this email is being written in a shared or delegated mailbox belonging to ${ownership.ownerEmail}${signedInAs}.`;
    }
    if (ownership.sharedMailbox) {
        return `Outlook says this email is being written in a shared or delegated mailbox${signedInAs}.`;
    }
    if (ownership.mailboxMismatch && ownership.fromEmail) {
        return `This email is being written from ${ownership.fromEmail}${signedInAs}.`;
    }
    return '';
};

/**
 * Turns an Office.AsyncResult failure into the fields support actually needs.
 * The host error carries the real reason (for example 5100 "previous call in
 * progress"), and the old code discarded it entirely.
 */
export const describeOfficeError = (result: any): any => {
    const out: { [key: string]: any } = { asyncStatus: result && result.status };
    const error = result && result.error;
    if (error) {
        out.officeErrorCode = error.code;
        out.officeErrorName = error.name;
        out.officeErrorMessage = error.message;
        if (isTransientOfficeError(result)) {
            out.transient = true;
        }
        if (isInsufficientItemPermissionsError(result)) {
            out.insufficientPermissions = true;
        }
    } else {
        out.officeErrorMessage = '(host reported a failure without an error object)';
    }
    return out;
};

/**
 * Environment facts that explain most startup failures: host and version,
 * which mailbox the add-in is running in, whether an item is present and in
 * compose mode, and whether the webview allows cookies. Every read is guarded
 * because at startup half of the Office object graph may not exist yet.
 *
 * userEmail is the mailbox Office bound the add-in to. Without it a report
 * says a mailbox refused the save but never which one, and support has to ask
 * the user to work that out by hand. It is the same address the link call
 * already sends, and startup failures happen before that call.
 */
export const collectHostDiagnostics = (): { [key: string]: any } => {
    const out: { [key: string]: any } = {};

    const read = (name: string, get: () => any) => {
        try {
            const value = get();
            if (value !== undefined && value !== null && value !== '') {
                out[name] = value;
            }
        } catch (ex) {
            out[name] = '(unavailable)';
        }
    };

    read('userEmail', () => Office.context.mailbox.userProfile.emailAddress);
    read('host', () => Office.context.mailbox.diagnostics.hostName);
    read('hostVersion', () => Office.context.mailbox.diagnostics.hostVersion);
    read('owaView', () => Office.context.mailbox.diagnostics.OWAView);
    read('platform', () => Office.context.platform);
    read('displayLanguage', () => Office.context.displayLanguage);
    read('mailboxApi', () => {
        // Highest supported Mailbox requirement set. saveAsync in compose
        // needs 1.3, and the NAA broker needs a recent host.
        const sets = ['1.15', '1.14', '1.13', '1.12', '1.10', '1.9', '1.8', '1.7', '1.6', '1.5', '1.4', '1.3', '1.1'];
        for (const set of sets) {
            if (Office.context.requirements.isSetSupported('Mailbox', set)) {
                return set;
            }
        }
        return 'below-1.1';
    });
    read('hasItem', () => Office.context.mailbox.item != null);
    read('itemMode', () => {
        const item: any = Office.context.mailbox.item;
        if (item == null) {
            return 'no-item';
        }
        // In compose mode subject is an async accessor object; in read mode
        // it is a plain string
        return typeof item.subject === 'object' ? 'compose' : 'read';
    });
    read('itemType', () => (Office.context.mailbox.item as any).itemType);
    read('online', () => navigator.onLine);
    read('cookiesEnabled', () => navigator.cookieEnabled);
    read('origin', () => window.location.origin);
    read('userAgent', () => navigator.userAgent);

    return out;
};

export const buildStartupDiagnostics = (
    code: string,
    phase: string,
    detail?: any,
    now: () => Date = () => new Date()): StartupDiagnostics => ({
        code,
        phase,
        version: getVersion(),
        at: now().toISOString(),
        host: collectHostDiagnostics(),
        detail,
    });

// Rendered into the technical-details box and posted to the server
export const formatStartupDiagnostics = (diagnostics: StartupDiagnostics): string =>
    truncate(safeStringify(diagnostics, 2), MAX_DIAGNOSTICS_LENGTH);

// Help article explaining every startup error code. Each code has its own
// anchor there; keep the anchors in sync with docs/SIGNINERRORS.html.
export const HELP_ARTICLE_URL = 'https://help.mailmerge365.com/article/117-application-errors';

export const buildHelpLink = (code: string): string =>
    `${HELP_ARTICLE_URL}#${(code || '').toLowerCase()}`;
